/**
 * SandraJoy Studios — "Purchase Original" button.
 *
 * Any link on the page whose address is a Shopify add-to-cart link
 * (…/cart/add?id=VARIANT_ID&quantity=1) is intercepted: the item is added
 * to the cart in the background and the theme's slide-out cart opens.
 *
 * If this script ever fails to load, the link still works on its own —
 * it adds the item and opens the full cart page instead.
 */
import { CartAddEvent } from '@theme/events';

const CART_ADD_PATTERN = /\/cart\/add\?/;

document.addEventListener('click', async (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const link = /** @type {HTMLAnchorElement | null} */ (target?.closest('a[href]'));
  if (!link || !CART_ADD_PATTERN.test(link.href)) return;

  const url = new URL(link.href, window.location.origin);
  const variantId = url.searchParams.get('id');
  if (!variantId) return;

  event.preventDefault();
  if (link.getAttribute('aria-busy') === 'true') return;
  link.setAttribute('aria-busy', 'true');

  const root = window.Shopify?.routes?.root || '/';
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter(Boolean);

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', url.searchParams.get('quantity') || '1');
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  try {
    const addResponse = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const added = await addResponse.json();

    const cart = await (await fetch(`${root}cart.js`)).json();
    const alreadyInCart = cart.items?.some((item) => String(item.variant_id) === String(variantId));

    // Not added and not already in the cart (e.g. sold): fall back to Shopify's own page and message.
    if (!addResponse.ok && !alreadyInCart) {
      window.location.href = link.href;
      return;
    }

    link.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: 'sjs-original-button',
        itemCount: 1,
        variantId,
        sections: addResponse.ok ? added.sections : undefined,
      })
    );

    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.href = `${root}cart`;
    }
  } catch (error) {
    console.error(error);
    window.location.href = link.href;
  } finally {
    link.removeAttribute('aria-busy');
  }
});
